/** Per-alias access-tag inputs for materialising an access-group template group.
 *
 * Shared by the RBAC MaterialiseDialog and the preflight ACCESS_GROUPS_SETUP control so the
 * arguments UX is identical in both places: each argument alias is a multi-select of the tenant's
 * access tags (free text creates a tag by name). One access group is created per combination of
 * argument values.
 */
import { Alert, Autocomplete, Chip, Stack, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { rbacApi } from '@/api/rbac';

export function AgroupArgumentsForm({
  aliases,
  topId,
  values,
  onChange,
  disabled,
}: {
  aliases: string[];
  /** Tenant whose access tags populate the options; null disables the inputs. */
  topId: number | null;
  values: Record<string, string[]>;
  onChange: (values: Record<string, string[]>) => void;
  disabled?: boolean;
}) {
  const tagsQuery = useQuery({
    queryKey: ['accessTags', topId],
    queryFn: () => rbacApi.listAccessTags(topId as number),
    enabled: topId !== null,
  });
  const tagOptions = (tagsQuery.data?.results ?? []).map((t) => t.name);

  if (aliases.length === 0) {
    return (
      <Alert severity="info">No arguments — each template generates exactly one access group.</Alert>
    );
  }

  return (
    <Stack spacing={2}>
      {aliases.map((alias) => (
        <Autocomplete
          key={alias}
          multiple
          freeSolo
          options={tagOptions}
          value={values[alias] ?? []}
          onChange={(_, v) => onChange({ ...values, [alias]: v as string[] })}
          disabled={disabled || topId === null}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip label={option} size="small" {...getTagProps({ index })} key={`${option}-${index}`} />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={`Values for “${alias}”`}
              placeholder="Existing tag or new tag name — press Enter to add"
              helperText="One access group is created per combination of argument values."
            />
          )}
        />
      ))}
    </Stack>
  );
}
